'use strict';

namespace Core.Customer {

    import CrossSellType = Core.Booking.CrossSellType;
    import BookedTrip = Core.ManageBooking.BookedTrip;
    import TripStage = Core.ManageBooking.TripStage;

    class CustomerDashboard {

        // Object literal defines which cross-sells are supported by each place holder.
        // Cross-sells are arranged in priority order.
        private crossSellsByPlaceholder = {
            2: [
                CrossSellType.Hotels,
                CrossSellType.Attractions
            ],
            4: [
                CrossSellType.Transfers,
                CrossSellType.CarHire,
                CrossSellType.Seats,
                CrossSellType.Baggage
            ],
            5: [
                CrossSellType.Transfers,
                CrossSellType.CarHire,
                CrossSellType.Seats,
                CrossSellType.Baggage
            ]
        };

        showDashboard: boolean;
        fetching: boolean;
        tripReady: boolean;
        user: any|null = null;
        trip: BookedTrip|null = null;
        lastViewedHotel: Hotels.StaticHotel|null = null;
        searches: any[] = [];
        destinations: Destinations.DestinationContentCard[] = [];

        crossSellOptions = {
            Hotels: <CrossSellHotelOption|null>null,
            Attractions: <CrossSellProductOption|null>null,
            Transfers: <CrossSellProductOption|null>null,
            CarHire: <CrossSellProductOption|null>null,
            Baggage: <CrossSellFlightExtraOption|null>null,
            Seats: <CrossSellFlightExtraOption|null>null
        };

        dashboardTemplate: string;

        tileTemplates = {
            1: <string|null>null,
            2: <string|null>null,
            3: <string|null>null,
            4: <string|null>null,
            5: <string|null>null
        };

        tileModels = {
            1: <any|null>null,
            2: <any|null>null,
            3: <any|null>null,
            4: <any|null>null,
            5: <any|null>null
        };

        tileTypes = {
            1: <DashboardTileType|null>null,
            2: <DashboardTileType|null>null,
            3: <DashboardTileType|null>null,
            4: <DashboardTileType|null>null,
            5: <DashboardTileType|null>null
        };

        constructor (
            private $rootScope,
            private $scope: ng.IScope,
            private $q: ng.IQService,
            private bookingsManagerService: ManageBooking.BookingsManagerService,
            private crossSellCommonService: any,
            private customerDashboardService: CustomerDashboardService,
            private houseRecommender: any,
            private currentUser: any,
            private authService: any
        ) {
            $scope.$on('auth:signin', () => {
                this.setDashboardState();
            });

            $scope.$on('auth:signout', () => {
                this.setDashboardState();
            });

            this.setDashboardState();
        }

        private setDashboardState(): void {
            // Set local scope user
            this.user = this.currentUser.get();
            // Show this dashboard if the user is signed in
            this.showDashboard = this.user.AnonSignedIn || this.user.SignedIn;
            this.showDashboard && this.fetch();
        }

        private fetch(): void {
            this.fetching = true;
            this.tripReady = false;

            this.resetData();

            let bookingId = this.user.AnonSignedIn && this.authService.getSignedInBookingId();
            let tripPromise: ng.IPromise<any> = bookingId
                    ? this.bookingsManagerService.getTrip(bookingId)
                    : this.bookingsManagerService.getCurrentTripForUser();

            tripPromise.then(trip => {
                this.trip = trip;

                // // DEBUG -- force trip stage
                // if (this.trip) {
                //     this.trip.TripStage = TripStage.PreTrip;
                // }

                // Set the dashboard template
                this.dashboardTemplate = this.customerDashboardService.getTemplateForDashboard(this.trip);

                // If appropriate, set the trip template whilst other content loads
                if (this.trip && (this.trip.TripStage === TripStage.PreTrip
                                    || this.trip.TripStage === TripStage.OnTrip)) {
                    this.tileTypes[1] = DashboardTileType.Trip;
                    this.tileTemplates[1] =
                        this.customerDashboardService.getTemplateForPlaceholder(DashboardTileType.Trip);
                    this.tripReady = true;
                }

                let promises: ng.IPromise<any>[] = [];

                if (this.trip && this.trip.TripStage === TripStage.PreTrip) {
                    // get cross sells
                    promises.push(this.customerDashboardService.getAllCrossSellOptions(this.trip.MainBookingId));
                } else {
                    promises.push(this.$q.when()); // dummy the cross-sell promise
                    // Get recent searches, last viewed hotel and recommended destinations
                    //promises.push(this.customerDashboardService.getRecentSearches(2));
                    promises.push(this.$q.when());
                    promises.push(this.customerDashboardService.getLastViewedHotel());
                    promises.push(this.houseRecommender.getHouseRecs());
                }

                this.$q.all(promises).then(([crossSells, searches, hotel, destinations]) => {

                    if (crossSells) {
                        // Assign any cross-sell options to our object literal
                        _.each(crossSells, (option: CrossSellOption) => {
                            this.crossSellOptions[CrossSellType[option.CrossSellType]] = option;
                        });
                    }

                    if (searches) {
                        this.searches = searches;
                    }
                    if (hotel) {
                        this.lastViewedHotel = hotel;
                    }
                    if (destinations) {
                        this.destinations = destinations;
                    }

                    // Now that we have all information, set the templates for each cell
                    this.configureTiles().then(() => {
                        // We are done - display everything else
                        this.fetching = false;
                        this.customerDashboardService.trackDashboardLoad(this.trip, this.tileTypes);
                    });

                });

            });

        }

        private configureTiles(): ng.IPromise<any> {
            let deferred = this.$q.defer();

            if (this.trip) {

                if (this.trip.TripStage === TripStage.PreTrip) {
                    // *** PRE TRIP ***

                    // Placeholder 1
                    // Trip
                    this.tileTypes[1] = DashboardTileType.Trip;

                    // Placeholders 2, 3, 4, 5
                    // Assign cross sell templates to placeholders according to our validity rules
                    let assignedTypes: CrossSellType[] = [];

                    // iterate placeholders which support cross-sells
                    _.forOwn(this.crossSellsByPlaceholder, (types: CrossSellType[], key: string) => {

                        // Take the first non-assigned cross-sell type which we have a cross-sell option for
                        let chosenType = _.find(types, (type: CrossSellType) => {
                            return this.crossSellOptions[CrossSellType[type]]
                                && assignedTypes.indexOf(type) === -1;
                        });
                        if (chosenType) {
                            // assign the placeholder type for this cross-sell
                            assignedTypes.push(chosenType);

                            this.tileTypes[key] = DashboardTileType[`CrossSell${CrossSellType[chosenType]}`];
                            this.tileModels[key] = this.crossSellOptions[CrossSellType[chosenType]];
                        }
                    });

                    // Placeholder 2 (if not populated by a cross-sell)
                    // Small App Promo
                    if (!this.tileTypes[2]) {
                        this.tileTypes[2] = DashboardTileType.AppPromo;
                    }

                    // Placeholder 3 (if not populated by a cross-sell)
                    // Insurance Confirmation OR Insurance Option
                    if (!this.tileTypes[3]) {
                        this.tileTypes[3] = this.trip.InsuranceBooking
                            ? DashboardTileType.InsuranceConf
                            : DashboardTileType.InsuranceOption;
                    }

                } else if (this.trip.TripStage === TripStage.OnTrip) {
                    // *** ON TRIP ***

                    // Placeholder 1
                    // Trip
                    // *** ALREADY SET ***



                    // Placeholder 2
                    // TR Contact
                    this.tileTypes[2] = DashboardTileType.ContactUs;

                    // Placeholder 3
                    // Insurance claim OR Travel guide
                    this.tileTypes[3] = this.trip.InsuranceBooking
                            ? DashboardTileType.InsuranceClaim
                            : DashboardTileType.TravelGuide;

                } else if (this.trip.TripStage === TripStage.PostTrip) {
                    // *** POST TRIP ***

                    // Placeholder 1
                    // Review your hotel OR App promo
                    this.tileTypes[1] =
                        this.trip.HotelBooking && !this.trip.HotelBooking.ReviewedByCustomer
                            ? DashboardTileType.ReviewHotel
                            : DashboardTileType.AppPromo;

                    // Placeholder 2
                    // Searches[0] OR Destinations[0]
                    if (this.searches[0]) {
                        this.tileTypes[2] = DashboardTileType.Search;
                        this.tileModels[2] = this.searches[0];
                    } else {
                        this.tileTypes[2] = DashboardTileType.Destination;
                        this.tileModels[2] = this.destinations[0];
                    }

                    // Placeholder 3
                    // Destinations[0] OR Destinations[1]
                    this.tileTypes[3] = DashboardTileType.Destination;
                    if (this.tileTypes[2] === DashboardTileType.Search) {
                        // show the first destination which doesn't match the destination of the search
                        this.tileModels[3] = this.customerDashboardService.getDestinationExcludingSearch(
                            this.tileModels[2],
                            this.destinations);
                    } else {
                        // show the second destination
                        this.tileModels[3] = this.destinations[1];
                    }
                }

            } else {
                // *** NO TRIP ***

                // Placeholder 1
                // App promo
                this.tileTypes[1] = DashboardTileType.AppPromo;

                // Placeholder 2
                // Last Viewed Hotel OR Searches[0] OR Destinations[0]
                if (this.lastViewedHotel) {
                    // show last viewed hotel
                    this.tileTypes[2] = DashboardTileType.LastHotel;
                    this.tileModels[2] = this.lastViewedHotel;
                } else if (this.searches[0]) {
                    // show first search
                    this.tileTypes[2] = DashboardTileType.Search;
                    this.tileModels[2] = this.searches[0];
                } else {
                    // show first destination
                    this.tileTypes[2] = DashboardTileType.Destination;
                    this.tileModels[2] = this.destinations[0];
                }

                // Placeholder 3
                // Searches[0] OR Destinations[0] OR Destinations[1]
                if (this.tileTypes[2] === DashboardTileType.LastHotel) {
                    // show first search OR first Destination
                    if (this.searches[0]) {
                        this.tileTypes[3] = DashboardTileType.Search;
                        this.tileModels[3] = this.searches[0];
                    } else {
                        this.tileTypes[3] = DashboardTileType.Destination;
                        this.tileModels[3] = this.destinations[0];
                    }
                } else if (this.tileTypes[2] === DashboardTileType.Search) {
                    this.tileTypes[3] = DashboardTileType.Destination;
                    // show the first destination which doesn't match the destination of the search
                    this.tileModels[3] = this.customerDashboardService.getDestinationExcludingSearch(
                        this.tileModels[2],
                        this.destinations);
                } else {
                    // show second destination
                    this.tileTypes[3] = DashboardTileType.Destination;
                    this.tileModels[3] = this.destinations[1];
                }

            }

            // map the templates to the types
            _.forOwn(this.tileTypes, (type: DashboardTileType, key: string) => {
                if (type) {
                    this.tileTemplates[key] = this.customerDashboardService.getTemplateForPlaceholder(type);
                }
            });

            deferred.resolve();
            return deferred.promise;
        }

        private resetData(): void {
            // reset all data to initial state
            this.trip = null;
            this.lastViewedHotel = null;
            this.searches.length = 0;
            this.destinations.length = 0;
            _.forOwn(this.crossSellOptions, o => o = null);

            // placeholder templates and models
            _.forOwn(this.tileTemplates, p => p = null);
            _.forOwn(this.tileModels, p => p = null);
            _.forOwn(this.tileTypes, p => p = null);
        }

        public getModelForPlaceholder(position: number): any[] {
            // We return the model in an array so that we can use ng-repeat
            // to declare a privately scoped model for the ng-included template
            return [this.tileModels[position]];
        }

        public crossSellClick(option: CrossSellProductOption): void {
            //mybTrack.track('MYB - Cross Sell Click');
            this.crossSellCommonService.startCrossSellJourney(option.BookingId, option.ProductDetails);
        }

        public tileClick(position: number): void {
            this.customerDashboardService.trackDashboardClick(position, this.tileTypes[position]);
        }
    }

    angular
        .module('core')
        .directive('customerDashboard', (): ng.IDirective => {
            return {
                restrict: 'E',
                scope: {},
                replace: true,
                controller: CustomerDashboard,
                controllerAs: 'cd',
                bindToController: {
                },
                templateUrl: 'partials/customer/customerdashboard'
            };
        });
}
